/* eslint-disable */
// src/lib/payroc/payrocHostedFields.ts
import { getFirebaseConfig } from '../../../../lib'

const {
    payrocUrl,
    payrocTestUrl,
} = getFirebaseConfig()

type AnyObj = Record<string, any>;

declare global {
    interface Window {
        Payroc?: any;
    }
}

type WrapperArgs = {
    onPreSubmit: AnyObj;
    fieldsConfig: AnyObj;
    styles?: AnyObj;
    sessionToken: string;
    testWorldnet: boolean;
    mode?: "payment" | string;
    cardholderFirstName?: string | null;
    cardholderLastName?: string | null;
    achAccountType?: string | null;
    nameOnAccount?: string | null;
    paymentType?: "card" | "ach" 
};

export class PayrocHostedFieldsWrapper {
    private onPreSubmit: AnyObj;
    private fieldsConfig: AnyObj;
    private styles?: AnyObj;
    private sessionToken: string;
    private testWorldnet: boolean;
    private mode: string;

    private paymentType: "card" | "ach" | null;

    // allow null
    private cardholderFirstName: string | null | undefined;
    private cardholderLastName: string | null | undefined;
    private cardholderName: string | null;
    private achAccountType: string | null;
    private nameOnAccount: string | null;
    public form: any = null;

    constructor({
        onPreSubmit,
        fieldsConfig,
        styles,
        sessionToken,
        testWorldnet,
        mode = "payment",
        cardholderFirstName = "",
        cardholderLastName = "",
        achAccountType = "checking",
        paymentType = "card",
        nameOnAccount = "",
    }: WrapperArgs) {
        this.onPreSubmit = onPreSubmit;
        this.fieldsConfig = fieldsConfig;
        this.styles = styles;
        this.sessionToken = sessionToken;
        this.testWorldnet = testWorldnet;
        this.mode = mode;
        this.nameOnAccount = nameOnAccount;
        this.paymentType = paymentType;
        this.achAccountType = achAccountType;
        const fullName = `${cardholderFirstName ?? ""} ${cardholderLastName ?? ""}`.trim();
        this.cardholderName = fullName.length ? fullName : null;
    }

    async init(): Promise<any> {
        if (typeof window === "undefined") {
            throw new Error("PayrocHostedFieldsWrapper can only run in the browser.");
        }

        const Payroc = window.Payroc;
        if (!Payroc?.hostedFields) {
            throw new Error("Payroc.hostedFields is not available (script not loaded?).");
        }

        const afterReady = (formInstance: any) => {
            this.form = formInstance;

            // decide which optional behavior to apply
            if (this.paymentType === "card") this._handleCardholderOptional();
            else this._handleACHTypeOptional(); // for ACH, we want to handle optional account type 

        };

        if (typeof Payroc.hostedFields.setup === "function") {
            return new Promise((resolve, reject) => {
                Payroc.hostedFields.setup({
                    fields: this.fieldsConfig,
                    styles: this.styles,
                    onReady: (formInstance: any) => {
                        afterReady(formInstance);
                        resolve(formInstance);
                    },
                    onError: reject,
                });
            });
        }

        if (typeof Payroc.hostedFields === "function") {
            const paymentForm = new Payroc.hostedFields({
                sessionToken: this.sessionToken,
                mode: this.mode,
                fields: { [this.paymentType ?? "card"]: this.fieldsConfig },
                styles: this.styles,
            });

            afterReady(paymentForm);

            if (typeof paymentForm.initialize === "function") paymentForm.initialize();
            return paymentForm;
        }

        throw new Error("Unsupported Payroc hostedFields API shape.");
    }

    private _handleCardholderOptional() {
        if (!this.form) return;

        if(this.onPreSubmit) {
            this.form.onPreSubmit = this.onPreSubmit;
            this.form.isCardholderNameMandatory = false;
        } else {
            // These hooks only work if the Payroc instance supports them.
            // Safe-guard so we don't crash if they don't exist.
            this.form.onPreSubmit = async () => {
                this.form.isCardholderNameMandatory = false;
                await this.submitCardholderName();
                return true;
            };
        }

        if (typeof this.form.on === "function") {
            this.form.on("fieldValidityChange", (e: any) => {
                if (e?.field === "cardholderName") {
                    const index = this.form.invalidFields?.indexOf?.("cardholderName");
                    if (index !== -1 && index != null) {
                        this.form.invalidFields.splice(index, 1);
                    }
                    if (
                        Array.isArray(this.form.submittedFields) &&
                        !this.form.submittedFields.includes("cardholderName")
                    ) {
                        this.form.submittedFields.push("cardholderName");
                    }
                }
            });
        }
    }

    private _buildCardholderName() {
        const fullName = `${this.cardholderFirstName ?? ""} ${this.cardholderLastName ?? ""}`.trim();
        return fullName.length ? fullName : null;
    }

    private _buildAccountholderName() {
        const fullName = `${this.cardholderFirstName ?? ""} ${this.cardholderLastName ?? ""}`.trim();
        return fullName.length ? fullName : null;
    }

    public setCardholderName(firstName?: string | null, lastName?: string | null) {
        this.cardholderFirstName = firstName ?? null;
        this.cardholderLastName = lastName ?? null;
        this.cardholderName = this._buildCardholderName();
    }

    public setAccountholderName(firstName?: string | null, lastName?: string | null) {
        console.log("setAccountholderName called with:", firstName, lastName);
        this.cardholderFirstName = firstName ?? null;
        this.cardholderLastName = lastName ?? null;
        this.nameOnAccount = this._buildAccountholderName();
    }

    private _handleACHTypeOptional() {
        if (!this.form) return;

        // These hooks only work if the Pareqyroc instance supports them.
        // Safe-guard so we don't crash if they don't exist.
        this.form.onPreSubmit = async () => {
            this.form.isACHAccountTypeMandatory = false;
            await this.setACHAccountType(this.achAccountType);
            return true;
        };

        console.log("Setting up ACH account type change listener");
        console.log("Initial ACH account type:", this.achAccountType);
        console.log("Form object:", this.form);
        if (typeof this.form.on === "function") {
            this.form.on("fieldValidityChange", (e: any) => {
                if (e?.field === "accountType") {
                    const index = this.form.invalidFields?.indexOf?.("accountType");
                    if (index !== -1 && index != null) {
                        this.form.invalidFields.splice(index, 1);
                    }
                    if (
                        Array.isArray(this.form.submittedFields) &&
                        !this.form.submittedFields.includes("accountType")
                    ) {
                        this.form.submittedFields.push("accountType");
                    }
                }
                if (e?.field === "nameOnAccount") {
                    const index = this.form.invalidFields?.indexOf?.("nameOnAccount");
                    if (index !== -1 && index != null) {
                        this.form.invalidFields.splice(index, 1);
                    }
                    if (
                        Array.isArray(this.form.submittedFields) &&
                        !this.form.submittedFields.includes("nameOnAccount")
                    ) {
                        this.form.submittedFields.push("nameOnAccount");
                    }
                }
            });
        }
    }

    async submitCardholderName() {
        if (!this.sessionToken || !this.cardholderName) return;

        const url = `${this.testWorldnet ? payrocTestUrl : payrocUrl}/merchant/api/v2/hosted-fields-sessions/${encodeURIComponent(this.sessionToken)}/fields`;

        // Use crypto.randomUUID() in modern browsers
        const idempotencyKey =
            typeof crypto !== "undefined" && "randomUUID" in crypto
                ? crypto.randomUUID()
                : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Idempotency-Key": idempotencyKey, // <-- required by Payroc
            },
            body: JSON.stringify({
                type: "cardholderName",
                cardholderName: this.cardholderName,
                hostedFieldsSessionId: this.sessionToken,
            }),
        });

        if (!response.ok) {
            const text = await response.text().catch(() => "");
            throw new Error(
                `Failed to submit cardholder name: ${response.status} ${response.statusText} - ${text} - sesstionToken: ${this.sessionToken}  cardholderName: ${this.cardholderName} uri: ${url}`
            );
        }

        return response.json();
    }

    async submitAccounHolderName(firstName: string | null, lastName: string | null) {
        console.log("calling async submitAccounHolderName:", firstName, lastName);
        if (!this.sessionToken || !this.nameOnAccount) return;
        const url = `${this.testWorldnet ? payrocTestUrl : payrocUrl}/merchant/api/v2/hosted-fields-sessions/${encodeURIComponent(this.sessionToken)}/fields`;

        // Use crypto.randomUUID() in modern browsers
        const idempotencyKey =
            typeof crypto !== "undefined" && "randomUUID" in crypto
                ? crypto.randomUUID()
                : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
        const accountHolderName = `${firstName ?? ""} ${lastName ?? ""}`.trim();
        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Idempotency-Key": idempotencyKey, // <-- required by Payroc
            },
            body: JSON.stringify({
                type: "nameOnAccount",
                nameOnAccount: accountHolderName,
                hostedFieldsSessionId: this.sessionToken,
            }),
        });
        return response.json();
    }

    async setACHAccountType(accountType: string | null) {
        console.log("Calling async setACHAccountType:", accountType);
        console.log("Setting ACH account type:", accountType);
        if (!this.sessionToken || !this.achAccountType) return;

        const url = `${this.testWorldnet ? payrocTestUrl : payrocUrl}/merchant/api/v2/hosted-fields-sessions/${encodeURIComponent(this.sessionToken)}/fields`;

        // Use crypto.randomUUID() in modern browsers
        const idempotencyKey =
            typeof crypto !== "undefined" && "randomUUID" in crypto
                ? crypto.randomUUID()
                : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Idempotency-Key": idempotencyKey, // <-- required by Payroc
            },
            body: JSON.stringify({
                type: "accountType",
                accountType: accountType,
            }),
        });

        if (!response.ok) {
            const text = await response.text().catch(() => "");
            throw new Error(
                `Failed to submit account type: ${response.status} ${response.statusText} - ${text} - sesstionToken: ${this.sessionToken}  accountType: ${accountType} uri: ${url}`
            );
        }

        console.log("ACH account type set successfully");
        console.log("Response from setting ACH account type:", response);

        return response.json();
    }

    async submitAccountHolderFirstLastName(firstName: string, lastName: string) {
        this.nameOnAccount = `${firstName} ${lastName}`.trim();
        return this.submitAccounHolderName(firstName, lastName);
    }

    async submitCardholderFirstLastName(firstName: string, lastName: string) {
        this.cardholderName = `${firstName} ${lastName}`.trim();
        return this.submitCardholderName();
    }

    /*  ???  */
    async submitForm() {
        if (!this.form) {
            throw new Error("Form not initialized");
        }
        return this.form?.submit();
    }

    destroy() {
        this.form?.destroy?.();
        this.form = null;
    }
}